import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, ViewChild, effect } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { IconService } from 'src/app/ui/icon.service';
import { NavItem, ThemeItem } from '../../../models/navigation.model';
import { Themes } from '../../../models/navigation.model';
import { changeFontFamily, toggleDarkMode } from 'src/themes/theme-utilities';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { NavSearchComponent } from '../nav-search/nav-search.component';
import { ThemeService, ThemeConfig } from 'src/app/services/theme.service';
import { ConfigService } from 'src/app/config.service';

declare global {
  interface Window {
    requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  }
}

@Component({
  selector: 'app-desktop-nav',
  templateUrl: './desktop-nav.component.html',
  styleUrl: './desktop-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DesktopNavComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  @Input({ required: true }) navItems: NavItem[] = [];
  @Input({ required: true }) accountMenuItems: MenuItem[] = [];
  @Input({ required: false }) username: string = '';
  @Input({ required: false }) themeItem?: ThemeItem;
  
  // User photo properties
  userPhotoUrl: string | null = null;
  showUserPhoto: boolean = false;

  @ViewChild('navSearch') navSearch!: NavSearchComponent
  @ViewChild('themePicker') themePicker: any;
  @ViewChild('accountMenu') accountMenu: any;
  @ViewChild('contextMenu') contextMenu: any;
  


  activeNavItem: NavItem | undefined;
  showLoginModal: boolean = false;
  showAccountMenu: boolean = false;
  isAccountMenuOpen: boolean = false;
  showSubmenu: number | null = null;
  showSubSubmenu: { [key: string]: number | null } = {};
  submenuDirection: 'left' | 'right' = 'right';
  subSubmenuDirections: { [key: string]: 'left' | 'right' } = {};
  private submenuTimeout: any = null;
  private subSubmenuTimeouts: { [key: string]: any } = {};
  
  selectedPalette: string = '';
  selectedFont: string = '';
  
  private menuItemsCache: Map<string, MenuItem[]> = new Map();
  
  // Context menu state
  contextMenuItems: MenuItem[] = [];
  contextMenuTarget: NavItem | null = null;

  constructor(
    public icons: IconService,
    private router: Router,
    public auth: LocalAuthService,
    public cdr: ChangeDetectorRef,
    private themeService: ThemeService,
    private configService: ConfigService) {
    router.events
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event) => {
        this.closeAllNavigationMenus();
        this.clearActiveNavItem();
        
        setTimeout(() => {
          this.closeAllNavigationMenus();
        }, 100);
      });

    // Effect to sync dropdown with theme service state
    effect(() => {
      const currentTheme = this.themeService.currentTheme();
      const currentFont = this.themeService.fontFamily();
      
      // Update selected palette (use theme name for dropdown)
      this.selectedPalette = currentTheme;
      
      // Update selected font
      this.selectedFont = currentFont;
      
      // Trigger change detection
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.preCacheMenuItems();
    
    this.initializeThemeDefaults();
    this.initializeUserPhoto();
  }

  ngOnChanges(): void {
    if (this.themeItem) {
      this.initializeThemeDefaults();
    }
  }
  
  
  private preCacheMenuItems(): void {
    const departmentsMenu = this.navItems?.find(item => item.label === 'DEPARTMENTS');
    if (departmentsMenu) {
      this.preCacheDepartmentsMenu(departmentsMenu);
    }
    
    this.navItems?.forEach(navItem => {
      if ((navItem.hasSubItems || navItem.menuItems) && navItem.label !== 'DEPARTMENTS') {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.getAndCacheMenuItems(navItem));
        } else {
          setTimeout(() => this.getAndCacheMenuItems(navItem), 0);
        }
      }
    });
  }

  private preCacheDepartmentsMenu(departmentsMenu: NavItem): void {
    if (departmentsMenu.menuItems && departmentsMenu.menuItems.length > 0) {
     
      const lightweightMenuItems = departmentsMenu.menuItems
        .filter(item => this.hasAccessToMenuItem(item)) // Filter based on roles
        .map(item => ({
          label: item.label,
          icon: item.icon,
          routerLink: item.routerLink,
          url: item.url,
          command: item.command,
         
          items: item.items ? this.createLazyNestedItems(item.items) : undefined
        }));
      
      departmentsMenu._cachedMenuItems = lightweightMenuItems;
    }
  }

 
  private createLazyNestedItems(nestedItems: any[]): any[] {
    return nestedItems
      .filter(item => this.hasAccessToMenuItem(item)) // Filter based on roles
      .map(item => ({
        label: item.label,
        icon: item.icon,
        routerLink: item.routerLink,
        url: item.url,
       
        command: item.routerLink ? () => {
          this.router.navigate([item.routerLink!]);
         
        } : item.url ? () => {
          this.navigateToExternalUrl(item.url!);
        } : item.command,
       
        items: item.items ? this.createLazyNestedItems(item.items) : undefined
      }));
  }

 
  private processNestedItemsOnDemand(parentItem: any): void {
    if (parentItem.items && Array.isArray(parentItem.items)) {
     
      parentItem.items.forEach((nestedItem: any) => {
        if (nestedItem.routerLink && !nestedItem.command) {
          nestedItem.command = () => {
            this.router.navigate([nestedItem.routerLink!]);
           
          };
        } else if (nestedItem.url && !nestedItem.command) {
          nestedItem.command = () => {
            this.navigateToExternalUrl(nestedItem.url!);
          };
        }
        
       
        if (nestedItem.items && Array.isArray(nestedItem.items)) {
          this.processNestedItemsOnDemand(nestedItem);
        }
      });
    }
  }

 
  onMenuOpen(navItem: NavItem): void {
    if (navItem.label === 'DEPARTMENTS' && navItem._cachedMenuItems) {
     
      navItem._cachedMenuItems.forEach((menuItem: any) => {
        if (menuItem.items && Array.isArray(menuItem.items)) {
          this.processNestedItemsOnDemand(menuItem);
        }
      });
    }
  }


  onMenuHide(navItem: NavItem): void {
    if (this.activeNavItem === navItem) {
      this.clearActiveNavItem();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.navSearch.focus();
    }, 0);
    
    this.ensureDepartmentsMenuCached();
  }
  
  private ensureDepartmentsMenuCached(): void {
    setTimeout(() => {
      const departmentsMenu = this.navItems?.find(item => item.label === 'DEPARTMENTS');
      if (departmentsMenu && !departmentsMenu._cachedMenuItems) {
        this.preCacheDepartmentsMenu(departmentsMenu);
      }
    }, 0);
  }
  
  private isDepartmentsMenuReady(): boolean {
    const departmentsMenu = this.navItems?.find(item => item.label === 'DEPARTMENTS');
    return departmentsMenu?._cachedMenuItems !== undefined;
  }

  trackByNavItem(index: number, navItem: NavItem): string {
    return navItem.label;
  }

  @HostListener('window:click', ['$event'])
  handleDesktopNavClick = (e: any) => {
    const clickedNav: boolean = e.target.closest('.navbar') !== null;
    const clickedThemePicker: boolean = e.target.closest('.p-popover') !== null;
    const clickedCustomDropdown: boolean = e.target.closest('.custom-dropdown-menu') !== null;

    if (!clickedNav && !clickedThemePicker && !clickedCustomDropdown) {
      this.closeAllNavigationMenus();
      this.clearActiveNavItem();
    }
  }

  onNavItemClick(event: Event, navItem: NavItem) {
    this.setActiveNavItem(navItem);
  }

  onMenuToggle(event: Event, navItem: NavItem) {
    this.setActiveNavItem(navItem);
  }

  toggleCustomMenu(navItem: NavItem, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.activeNavItem === navItem) {
      this.clearActiveNavItem();
    } else {
      // Close all other menus when switching to a different nav item
      this.closeSearchAndAccountMenus();
      this.showSubmenu = null;
      this.showSubSubmenu = {};
      
      this.setActiveNavItem(navItem);
      this.onMenuOpen(navItem);
    }
  }

  closeSearchAndAccountMenus() {
    // Close search menu
    if (this.navSearch && this.navSearch.closeSearchPopover) {
      this.navSearch.closeSearchPopover();
    }
    
    if (this.navSearch) {
      this.navSearch.isSearchActive = false;
    }
    
    // Close account menu
    if (this.accountMenu && this.accountMenu.hide) {
      this.accountMenu.hide();
    }
    
    this.showAccountMenu = false;
    this.isAccountMenuOpen = false;
    
    // Close theme picker
    if (this.themePicker && this.themePicker.hide) {
      this.themePicker.hide();
    }
  }

  closeAccountAndThemeMenus() {
    // Close account menu
    if (this.accountMenu && this.accountMenu.hide) {
      this.accountMenu.hide();
    }
    
    this.showAccountMenu = false;
    this.isAccountMenuOpen = false;
    
    // Close theme picker
    if (this.themePicker && this.themePicker.hide) {
      this.themePicker.hide();
    }
  }

  closeAllMenusExceptSearch() {
    if (this.themePicker && this.themePicker.hide) {
      this.themePicker.hide();
    }
    
    if (this.accountMenu && this.accountMenu.hide) {
      this.accountMenu.hide();
    }
    
    this.showAccountMenu = false;
    this.isAccountMenuOpen = false;
    
    this.clearActiveNavItem();
  }

  closeAllMenus() {
    if (this.themePicker && this.themePicker.hide) {
      this.themePicker.hide();
    }
    
    if (this.navSearch && this.navSearch.closeSearchPopover) {
      this.navSearch.closeSearchPopover();
    }
    
    if (this.navSearch) {
      this.navSearch.isSearchActive = false;
    }
    
    if (this.accountMenu && this.accountMenu.hide) {
      this.accountMenu.hide();
    }
    
    this.showAccountMenu = false;
    this.isAccountMenuOpen = false;
    
    this.closeAllNavigationMenus();
    
    this.clearActiveNavItem();
  }



  private closeAllNavigationMenus(): void {
    // Custom dropdown menus are handled by component state
    // No need to manipulate DOM elements directly
    this.clearActiveNavItem();
  }



  convertNavItemToMenuItem(navItem: NavItem): MenuItem {
    const menuItem: MenuItem = {
      label: navItem.label,
      icon: navItem.icon ? `fa-solid ${navItem.icon.iconName}` : undefined,
      routerLink: navItem.routerLink,
      command: navItem.routerLink ? () => {
        this.router.navigate([navItem.routerLink!]);
        this.clearActiveNavItem();
      } : navItem.url ? () => {
        this.navigateToExternalUrl(navItem.url!);
      } : undefined
    };

    if (navItem.url) {
      menuItem.label = `${navItem.label}`;
    }
    if (navItem.menuItems && navItem.menuItems.length > 0) {
      menuItem.items = navItem.menuItems.map(item => {
        const processedItem: any = {
          ...item,
          command: item.routerLink ? () => {
            this.router.navigate([item.routerLink!]);
          } : item.url ? () => {
            this.navigateToExternalUrl(item.url!);
          } : item.command,
          label: item.url ? `${item.label}` : item.label
        };
        
        if (item.icon && typeof item.icon === 'string') {
          processedItem.icon = item.icon;
        } else {
          delete processedItem.icon;
        }
        
        return processedItem;
      });
    }

    return menuItem;
  }

  getAndCacheMenuItems(navItem: NavItem): MenuItem[] {
    if (navItem._cachedMenuItems) {
      return navItem._cachedMenuItems;
    }
    
    let menuItems: MenuItem[] = [];
    
    if (navItem.menuItems && navItem.menuItems.length > 0) {
      menuItems = navItem.menuItems
        .filter(item => this.hasAccessToMenuItem(item)) // Filter based on roles
        .map(item => {
          const processedItem: MenuItem = {
            ...item,
            command: item.routerLink ? () => {
              this.router.navigate([item.routerLink!]);
            } : item.url ? () => {
              this.navigateToExternalUrl(item.url!);
            } : item.command,
            label: item.url ? `${item.label}` : item.label
          };
          
          if (item.icon && typeof item.icon === 'string') {
            processedItem.icon = item.icon;
          } else {
            delete processedItem.icon;
          }
          
          return processedItem;
        });
    }
    
    navItem._cachedMenuItems = menuItems;
    return menuItems;
  }

  getMenuItemsFromNavItem(navItem: NavItem): MenuItem[] {
    return this.getAndCacheMenuItems(navItem);
  }

  hasAccessToMenuItem(item: any): boolean {
    // If no roles are specified, the item is accessible to everyone
    if (!item.roles || !Array.isArray(item.roles) || item.roles.length === 0) {
      return true;
    }
    
    // Check if user has any of the required roles
    return this.auth.hasAnyRole(item.roles);
  }

  setActiveNavItem(navItem: NavItem) {
    this.activeNavItem = navItem;
    this.cdr.detectChanges();
  }

  clearActiveNavItem() {
    this.activeNavItem = undefined;
    this.showSubmenu = null;
    this.showSubSubmenu = {};
    this.cdr.detectChanges();
  }

  clearActiveNavItemAndMenus() {
    this.clearActiveNavItem();
  }

  handleAccountClick(event: Event) {
    if (this.isAccountMenuOpen) {
      this.isAccountMenuOpen = false;
      this.accountMenu.hide();
      return;
    }
    
    this.clearActiveNavItem();
    
    this.signInClicked();
    
    if (this.auth.currentUser) {
      this.isAccountMenuOpen = true;
      this.accountMenu.toggle(event);
    }
  }

  signInClicked() {
    if (this.auth.currentUser) {
      this.toggleAccountMenu();
    } else {
      this.toggleLoginModal();
    }
  }

  toggleAccountMenu() {
    this.showAccountMenu = !this.showAccountMenu;
  }

  toggleLoginModal() {
    this.showLoginModal = !this.showLoginModal;
  }

  toggleDarkMode = () => {
    this.themeService.toggleDarkMode();
  };

  private initializeThemeDefaults(): void {
    // Initialize from theme service
    this.selectedPalette = this.themeService.currentTheme();
    this.selectedFont = this.themeService.fontFamily();
  }

  private initializeUserPhoto(): void {
    const user = this.auth.currentUser;
    
    
    if (user && user.newstaffId) {
      // Use the proper base URL from environment configuration
      const staffPhotoBaseUrl = this.configService.staffPhotoUrl;
      const photoUrl = `${staffPhotoBaseUrl}${user.newstaffId}.jpg?no_cache=${Date.now()}`;
      
     
      
      // Test if the image exists by creating an Image object
      const img = new Image();
      img.onload = () => {
      
        this.userPhotoUrl = photoUrl;
        this.showUserPhoto = true;
        this.cdr.detectChanges();
      };
      img.onerror = (error) => {
        
        // Photo doesn't exist, fall back to avatar
        this.userPhotoUrl = null;
        this.showUserPhoto = false;
        this.cdr.detectChanges();
      };
      img.src = photoUrl;
    } else {
     
      this.userPhotoUrl = null;
      this.showUserPhoto = false;
    }
  }

  onPaletteChange(e: any) {
    if (e?.value) {
      this.themeService.setTheme(e.value);
      this.selectedPalette = e.value;
    }
  }

  onFontChange(e: any) {
    if (e?.value) {
      this.themeService.setFontFamily(e.value);
      this.selectedFont = e.value;
    }
  }

  /** Palettes in shared ThemeNavPicker format (apply = paletteFunction). */
  get sharedThemePalettes(): { name: string; displayName?: string; apply: () => void }[] {
    if (!this.themeItem?.palettes?.length) return [];
    return this.themeItem.palettes.map((p) => ({
      name: p.name,
      displayName: p.displayName,
      apply: p.paletteFunction,
    }));
  }

  onThemeChangeFromPicker(name: string): void {
    this.selectedPalette = name;
    this.cdr.detectChanges();
  }

  toggleThemePicker(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.activeNavItem === this.themeItem) {
      this.clearActiveNavItem();
    } else {
      // Close account menu and search when opening theme picker
      this.closeAccountAndThemeMenus();
      // Close search menu
      if (this.navSearch && this.navSearch.closeSearchPopover) {
        this.navSearch.closeSearchPopover();
      }
      if (this.navSearch) {
        this.navSearch.isSearchActive = false;
      }
      this.setActiveNavItem(this.themeItem!);
    }
    
    this.themePicker.toggle(event);
  }

  onThemePickerHide() {
    if (this.activeNavItem === this.themeItem) {
      this.clearActiveNavItem();
    }
  }

  onAccountMenuHide() {
    this.isAccountMenuOpen = false;
  }

  handleSearchClick() {
    // Close account menu and theme picker when opening search
    this.closeAccountAndThemeMenus();
    this.clearActiveNavItem();
  }

  handleMenuItemClick(item: any, event: Event) {
    const mouseEvent = event as MouseEvent;
    
    // Check if Ctrl/Cmd key is pressed (for opening in new tab)
    const isCtrlClick = mouseEvent.ctrlKey || mouseEvent.metaKey;
    
    // Only prevent default if it's not a Ctrl+click
    if (!isCtrlClick) {
      event.preventDefault();
    }
    event.stopPropagation();
    
    // Only close menus if this item actually triggers navigation
    // Don't close if the item has nested items (sub-submenus)
    if (item.url || item.routerLink || (item.command && typeof item.command === 'function')) {
      this.closeAllNavigationMenus();
      this.clearActiveNavItem();
    }
    
    if (item.url) {
      if (isCtrlClick) {
        // For Ctrl+click, let the browser handle it naturally
        window.open(item.url, '_blank', 'noopener,noreferrer');
      } else {
        this.navigateToExternalUrl(item.url);
      }
      return;
    }
    
    if (item.routerLink) {
      if (isCtrlClick) {
        // For Ctrl+click on internal links, open in new tab
        const url = this.router.serializeUrl(this.router.createUrlTree([item.routerLink], { queryParams: item.queryParams }));
        window.open(this.configService.baseUrl + url, '_blank', 'noopener,noreferrer');
      } else {
        this.router.navigate([item.routerLink], { queryParams: item.queryParams });
      }
      return;
    }
    
    if (item.command && typeof item.command === 'function') {
      item.command();
      return;
    }
  }

  navigateToExternalUrl(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private getBasePath(): string {
    const currentPath = window.location.pathname;
    // Find the base path by looking for '/incident-management' in the current path
    const basePathMatch = currentPath.match(/^(\/incident-management)/);
    return basePathMatch ? basePathMatch[1] : '';
  }

  getRouterLinkHref(navItem: NavItem): string {
    if (navItem.routerLink) {
      const url = this.router.serializeUrl(this.router.createUrlTree([navItem.routerLink], { queryParams: navItem.queryParams }));
      return this.configService.baseUrl + url;
    }
    return '#';
  }

  handleInternalLinkClick(event: Event, navItem: NavItem) {
    const mouseEvent = event as MouseEvent;
    const isCtrlClick = mouseEvent.ctrlKey || mouseEvent.metaKey;
    
    if (!isCtrlClick) {
      event.preventDefault();
      this.router.navigate([navItem.routerLink!], { queryParams: navItem.queryParams });
    }
    // For Ctrl+click, let the browser handle it naturally with the href
  }

  onMenuItemMouseEnter(item: any, index: number, event: Event) {
    // Clear any existing timeout
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
    
    this.showSubmenu = item.items && item.items.length > 0 ? index : null;
    
    // Calculate submenu direction after a brief delay to ensure DOM is updated
    if (this.showSubmenu !== null) {
      setTimeout(() => {
        this.calculateSubmenuDirection(event.target as HTMLElement);
      }, 0);
    }
    
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'var(--p-primary-50)';
    }
  }

  private calculateSubmenuDirection(menuItemElement: HTMLElement,subSubMenuKey?: string) {
    const rect = menuItemElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const submenuWidth = 224; // min-width: 14rem (14 * 16px)
    
    // Check if there's enough space on the right
    const spaceOnRight = viewportWidth - rect.right;
    
    // If not enough space on the right, position to the left
    if (subSubMenuKey === undefined) {
      this.submenuDirection = spaceOnRight < submenuWidth ? 'left' : 'right';
    }
    else if (subSubMenuKey !== null) {
      this.subSubmenuDirections[subSubMenuKey] = spaceOnRight < submenuWidth ? 'left' : 'right';
    }
    
    this.cdr.detectChanges();
  }


  onMenuItemMouseLeave(event: Event) {
    // Don't automatically close submenus on mouse leave
    // They will only close when other items are opened or background is clicked
    
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'transparent';
    }
  }

  onSubMenuItemMouseEnter(event: Event) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'var(--p-primary-50)';
    }
  }

  onSubMenuItemMouseLeave(event: Event) {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'transparent';
    }
  }

  onSubSubMenuItemMouseEnter(event: Event) {
    // For third-level items, just handle the hover effect
    // Don't interfere with parent menu logic
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'var(--p-primary-50)';
    }
  }

  onSubSubMenuItemMouseLeave(event: Event) {
    // For third-level items, just handle the hover effect
    // Don't interfere with parent menu logic
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'transparent';
    }
  }

  onSubSubmenuContainerMouseEnter(parentIndex: number, subIndex: number) {
    // Clear any timeout when hovering over the sub-submenu container
    const key = `${parentIndex}-${subIndex}`;
    if (this.subSubmenuTimeouts[key]) {
      clearTimeout(this.subSubmenuTimeouts[key]);
      this.subSubmenuTimeouts[key] = null;
    }
  }

  onSubSubmenuContainerMouseLeave(parentIndex: number, subIndex: number) {
    // Don't automatically close sub-submenus when leaving the container
    // They will only close when other items are opened or background is clicked
    
    const key = `${parentIndex}-${subIndex}`;
    
    // Clear any existing timeout for this sub-submenu
    if (this.subSubmenuTimeouts[key]) {
      clearTimeout(this.subSubmenuTimeouts[key]);
      this.subSubmenuTimeouts[key] = null;
    }
  }

  onSubmenuContainerMouseEnter() {
    // Clear any timeout when hovering over the submenu container
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
  }

  onSubmenuContainerMouseLeave() {
    // Don't automatically close submenus when leaving the container
    // They will only close when other items are opened or background is clicked
    
    // Clear all sub-submenu timeouts to prevent delayed closing
    Object.keys(this.subSubmenuTimeouts).forEach(key => {
      if (this.subSubmenuTimeouts[key]) {
        clearTimeout(this.subSubmenuTimeouts[key]);
        this.subSubmenuTimeouts[key] = null;
      }
    });
  }

  onSubMenuItemMouseEnterWithNesting(subItem: any, subIndex: number, parentIndex: number, event: Event) {
    // Clear any timeout when hovering over a submenu item that has nested items
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
    
    // Clear any specific sub-submenu timeout for this item
    const key = `${parentIndex}-${subIndex}`;
    if (this.subSubmenuTimeouts[key]) {
      clearTimeout(this.subSubmenuTimeouts[key]);
      this.subSubmenuTimeouts[key] = null;
    }
    
    // Show sub-submenu if this item has nested items
    if (subItem.items && subItem.items.length > 0) {
      // Check if this sub-submenu is already open
      const isAlreadyOpen = this.showSubSubmenu[key] === subIndex;
      
      if (!isAlreadyOpen) {
        // Close all other sub-submenus and open this one
        // This ensures only one sub-submenu is open at a time
        this.showSubSubmenu = {};
        this.showSubSubmenu[key] = subIndex;
      }
      // If it's already open, don't do anything - just keep it open
      
      // Calculate sub-submenu direction
      setTimeout(() => {
        this.calculateSubmenuDirection(event.target as HTMLElement, key);
      }, 0);
    } else {
      // If this item doesn't have nested items, close all sub-submenus
      this.showSubSubmenu = {};
    }
    
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'var(--p-primary-50)';
    }
  }

  onSubMenuItemMouseLeaveWithNesting(subItem: any, subIndex: number, parentIndex: number, event: Event) {
    // Don't set timeout to close sub-submenu when leaving the parent item
    // The sub-submenu should stay open when hovering over its parent item
    // Only close it when actually leaving the sub-submenu container or moving to a different parent item
    
    const target = event.target as HTMLElement;
    if (target) {
      target.style.backgroundColor = 'transparent';
    }
  }

  // Context menu methods
  onNavItemRightClick(event: MouseEvent, navItem: NavItem) {
    event.preventDefault();
    event.stopPropagation();
    
    // Only show context menu for items that have a URL or routerLink
    if (!navItem.url && !navItem.routerLink) {
      return;
    }
    
    this.contextMenuTarget = navItem;
    this.contextMenuItems = this.getContextMenuItemsForNavItem(navItem);
    
    if (this.contextMenu && this.contextMenuItems.length > 0) {
      this.contextMenu.show(event);
    }
  }

  onMenuItemRightClick(event: MouseEvent, menuItem: MenuItem) {
    event.preventDefault();
    event.stopPropagation();
    
    // Only show context menu for items that have a URL or routerLink
    if (!menuItem.url && !menuItem.routerLink) {
      return;
    }
    
    this.contextMenuItems = this.getContextMenuItemsForMenuItem(menuItem);
    
    if (this.contextMenu && this.contextMenuItems.length > 0) {
      this.contextMenu.show(event);
    }
  }

  getContextMenuItemsForNavItem(navItem: NavItem): MenuItem[] {
    const items: MenuItem[] = [];
    
    if (navItem.routerLink) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([navItem.routerLink], { queryParams: navItem.queryParams })
      );
      const fullUrl = this.configService.baseUrl + url;
      
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(fullUrl, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          const fullUrlWithBase = this.configService.baseUrl + url;
          this.copyToClipboard(fullUrlWithBase);
        }
      });
    } else if (navItem.url) {
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(navItem.url!, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(navItem.url!, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          this.copyToClipboard(navItem.url!);
        }
      });
    }
    
    return items;
  }

  getContextMenuItemsForMenuItem(menuItem: MenuItem): MenuItem[] {
    const items: MenuItem[] = [];
    
    if (menuItem.routerLink) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([menuItem.routerLink], { queryParams: menuItem.queryParams })
      );
      const fullUrl = this.configService.baseUrl + url;
      
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(fullUrl, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          const fullUrlWithBase = this.configService.baseUrl + url;
          this.copyToClipboard(fullUrlWithBase);
        }
      });
    } else if (menuItem.url) {
      items.push({
        label: 'Open in New Tab',
        icon: 'pi pi-external-link',
        command: () => {
          window.open(menuItem.url!, '_blank', 'noopener,noreferrer');
        }
      });
      
      items.push({
        label: 'Open in New Window',
        icon: 'pi pi-window-maximize',
        command: () => {
          window.open(menuItem.url!, '_blank', 'width=1200,height=800,noopener,noreferrer');
        }
      });
      
      items.push({
        separator: true
      });
      
      items.push({
        label: 'Copy Link',
        icon: 'pi pi-copy',
        command: () => {
          this.copyToClipboard(menuItem.url!);
        }
      });
    }
    
    return items;
  }

  private async copyToClipboard(text: string): Promise<void> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Failed to copy text:', err);
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  }

  ngOnDestroy(): void {
    // Clean up timeouts
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
    }
    
    // Clean up all sub-submenu timeouts
    Object.keys(this.subSubmenuTimeouts).forEach(key => {
      if (this.subSubmenuTimeouts[key]) {
        clearTimeout(this.subSubmenuTimeouts[key]);
      }
    });
    
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
